/**
 * WalkthroughOverlay — Full-screen overlay with animated walkthrough experience.
 * Renders when a walkthrough is active.
 */
import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useWalkthrough } from "@/hooks/walkthroughs";
import WalkthroughCard from "./WalkthroughCard";
import StepIndicator from "./StepIndicator";

export default function WalkthroughOverlay() {
  const { activeWalkthrough, currentStep, nextStep, prevStep, skipWalkthrough, dismissWalkthrough } = useWalkthrough();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!activeWalkthrough) return;
      if (e.key === "Escape") dismissWalkthrough();
      if (e.key === "ArrowRight" || e.key === "Enter") nextStep();
      if (e.key === "ArrowLeft") prevStep();
    },
    [activeWalkthrough, dismissWalkthrough, nextStep, prevStep],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (!activeWalkthrough) return null;

  const step = activeWalkthrough.steps[currentStep];
  if (!step) return null;

  const isFirst = currentStep === 0;
  const isLast = currentStep === activeWalkthrough.steps.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        key="walkthrough-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center"
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={dismissWalkthrough}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center gap-6 px-4 max-w-lg w-full">
          <WalkthroughCard
            step={step}
            isFirst={isFirst}
            isLast={isLast}
            onNext={nextStep}
            onPrev={prevStep}
            onSkip={skipWalkthrough}
          />
          <StepIndicator
            totalSteps={activeWalkthrough.steps.length}
            currentStep={currentStep}
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
